import type { i18n as I18nInstance } from 'i18next';
import buildingIcon from '/business/shared/images/icons/bp-building-city-pictograms-bp-green-.svg';
import { basketTableTotalTypes, questionScopeCategories, questionSelectorOptions } from '/business/shared/util/Statics';

import type { BasketTableAnswerItem, QuestionConfigQuestionType } from '/index';
import formatNumber from '/shared/functions/formatNumber';

import useFormatLeasedBuildingsSharedCustom from '/business/shared/components/pages/question/formats/customDataFormats/useFormatLeasedBuildingsSharedCustom';
import useFormatLeasedBuildingsSharedSupplier from '/business/shared/components/pages/question/formats/supplierDataFormats/useFormatLeasedBuildingsSharedSupplier';
import { bottomContentsSupplier } from '/business/shared/components/questions/scope3/_education/bottomScope3';
import educationContentJson from '/business/shared/components/questions/scope3/upstreamBuildings/_education/upstreamLeasedBuildings.json';
import useUpstreamLeasedBuildingsSchema from '/business/shared/components/questions/scope3/upstreamBuildings/_hooks/useUpstreamLeasedBuildingsSchema';
import ReportingPeriod from '/business/shared/components/widgets/reporting-period/ReportingPeriod';
import initiativeCardBackground from '/business/shared/images/tip-img-nov2024/Upstream leased buildings card image.jpg';
import initiativeBackground from '/business/shared/images/tip-img-nov2024/Upstream leased buildings card image@2x.jpg';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import StyledParagraph from '/shared/styled/components/StyledParagraph';

export const getConfigureUpstreamLeasedBuildings = ({ i18n }: { i18n: I18nInstance }): QuestionConfigQuestionType => {
  return {
    id: 'upstream-leased-buildings',
    title: 'Upstream leased buildings',
    apiKey: 'calculation-upstream-leased-buildings',
    primaryI18nKey: 'business:QuestionPage.forms.emissionSelector.upstreamLeasedBuildings',
    subTextI18nKey: 'business:QuestionPage.forms.emissionSelector.upstreamLeasedBuildingsTip',
    overviewPageTitleI18nKey: 'business:QuestionPage.forms.emissionSelector.upstreamLeasedBuildings',
    icon: buildingIcon,
    useHookLoader: useUpstreamLeasedBuildingsSchema,
    scopeCategory: questionScopeCategories.SCOPE_3,
    isCompletePropName: 'isUpstreamLeasedBuildingsComplete',
    initiative: {
      getVisuals: () => {
        return {
          description: false,
          background: initiativeBackground,
          useMask: false,
          cardBackground: initiativeCardBackground,
        };
      },
    },
    table: {
      title: 'Calculated carbon emissions for upstream leased buildings data: ',
      totalType: basketTableTotalTypes.TONNAGE,
      headings: ['Building identifier', 'Emission source', 'Quantity', 'Carbon emissions'].filter((item) => !!item),
      cells: [
        'buildingIdentifier',
        ({ fuelType }: { fuelType: { displayName: string } | null }) => fuelType?.displayName,
        ({
          quantity,
          fuelUnit = 'units',
          harmonisedInjection,
        }: {
          quantity: number;
          fuelUnit?: string;
          harmonisedInjection: (unit: string) => string;
        }) =>
          `${quantity} ${i18n.t('business:emissionFactors.volumes.'.concat(fuelUnit), fuelUnit)} ${harmonisedInjection(fuelUnit)}`,
        ({ tonnage }: { tonnage: number }) => `${formatNumber(tonnage)} tCO\u2082e`,
      ].filter((item) => !!item),
      isAnswerEditable: true,
      supplier: {
        columns: [
          'buildingIdentifier',
          'emissionSource',
          'supplierIntensityDataOrAbsoluteEmissions',
          'quantity',
          'carbonEmissions',
        ],
      },
      custom: {
        columns: ['buildingIdentifier', 'emissionSource', 'intensityValue', 'unitOfMeasurement', 'carbonEmissions'],
      },
    },
    selector: {
      tableViewSummaryRowParser: (allAnswerRows: BasketTableAnswerItem[]) => {
        const combined = allAnswerRows.reduce((prev: { identifier: string; tonnage: number }[], current) => {
          const genericData = current?.answer?.genericData || {};
          const id = (genericData.buildingIdentifier || genericData.building_identifier) as string | undefined;
          if (!id) {
            return prev;
          }
          const locatorIndex = prev.findIndex((search) => {
            return search.identifier === id;
          });
          if (locatorIndex === -1) {
            prev.push({
              identifier: id,
              tonnage: current.answer?.tonnage || 0,
            });
          } else {
            prev[locatorIndex].tonnage += current.answer?.tonnage || 0;
          }
          return prev;
        }, []);

        return combined.map((combinedRow): BasketTableAnswerItem => {
          return {
            itemId: combinedRow.identifier,
            cells: [combinedRow.identifier, `${formatNumber(combinedRow.tonnage)} tCO\u2082e`],
            type: 'upstream-leased-buildings',
            title: combinedRow.identifier,
            answer: {
              tonnage: combinedRow.tonnage,
            },
          };
        });
      },
      injectQuestionBranchSelector: true,
      injectSummaryView: true,
      questionBranchEnableCustomData: true,
      questionBranchEnableSupplierData: true,
      para: 'To calculate emissions associated with buildings leased or rented by your business where the landlord controls and pays for energy and fuel use, you can use a combination of activity data, supplier data, and custom data.',
      options: [
        {
          title: questionSelectorOptions.ACTIVITY,
          requiredData: 'Building identifier, type of fuel, quantity used during the reporting period.',
        },
        {
          title: questionSelectorOptions.SUPPLIER,
          requiredData:
            'Building identifier, type of fuel, supplier name and intensity data/absolute emissions, quantity.',
        },
        {
          title: questionSelectorOptions.CUSTOM,
          requiredData: 'Building identifier, emission source, intensity data/absolute emissions, quantity.',
        },
      ],
    },
    education: {
      hasEducationWidget: true,
      educationContent: educationContentJson,
      bottomContentsSupplier: bottomContentsSupplier,
    },
    useQuestionBranchSupplierLoader: () =>
      useFormatLeasedBuildingsSharedSupplier({
        questionFormatName: 'upstream-leased-buildings',
      }),
    useQuestionBranchCustomLoader: () =>
      useFormatLeasedBuildingsSharedCustom({ questionFormatName: 'upstream-leased-buildings' }),
    supplierAndCustomSubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          Enter the estimated energy and fuel consumption for any buildings or part of a building leased or rented by
          your business during the reporting period: <ReportingPeriod $green />, where the landlord controls and pays
          for energy and fuel use.
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
    activitySubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          What is the estimated energy and fuel consumption for any buildings or part of a building leased or rented by
          your business during the reporting period: <ReportingPeriod $green />, where the landlord controls and pays
          for energy and fuel use?
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
  };
};
